import { AppRoutingModule } from './app-routing.module';
import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RouteReuseStrategy } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSliderModule } from '@angular/material/slider';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { MatInputModule } from '@angular/material/input';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';

// components
import { AppComponent } from './app.component';
import { FooterComponent } from '../components/footer/footer.component';
import { LandingPageComponent } from './landing/landing.component';
import { MarketingPageComponent } from './marketing/marketing.component';
import { AlertComponent } from '../components/alert-modal/alert-modal.component';
import { InfoAlertComponent } from '../components/info-alert/info-alert.component';
import { DialogComponent } from '../components/dialog-alert/dialog-alert.component';
import { ContactComponent } from '../components/footer/contact/contact.component';
import { AccountComponent } from './account/account.component';
import { PreferencesComponent } from '../components/preferences/preferences.component';
import { TutorialsComponent } from '../components/tutorials/tutorials.component';
import { WelcomeMessageComponent } from './welcome-message/welcome-message.component';
import { CarouselComponent } from './carousel/carousel.component';
import { SettingsComponent } from './settings/settings.component';
import { SubscriptionComponent } from './settings/subscription/subscription.component';
import { NotificationsDropdownComponent } from './notifications-dropdown/notifications-dropdown.component';

// utils
import { CustomReuseStrategy } from './reuse-strategy';
import { CheckboxListValueAccessorDirective } from './checkbox-list-value-accessor';

import { ImageDialogComponent } from '../components/image-dialog/image-dialog.component';
import { DatasourceComponent } from './datasource/datasource.component';
import { FeatureTileComponent } from '../components/feature-tile/feature-tile.component';
import { HoursMinutesEntryComponent } from '../components/hours-minutes-entry/hours-minutes-entry.component';

import { ProfileCreationBackgroundComponent } from '../components/profile-creation-background/profile-creation-background.component';
import { MobileAppDownloadCtaComponent } from '../components/mobile-app-download-cta/mobile-app-download-cta.component';
import { httpInterceptorProviders } from '../http-interceptors';
import { SharedModule } from './_shared/shared.module';
import { WidgetsComponent } from './widgets/widgets.component';
import { DownloadAppLinksComponent } from './widgets/download-app-links/download-app-links.component';
import { MyRewardsWidgetComponent } from './widgets/my-rewards-widget/my-rewards-widget.component';
import { CompetitionWidgetComponent } from './widgets/competition-widget/competition-widget.component';
import { LastBadgeEarnedComponent } from './widgets/last-badge-earned/last-badge-earned.component';
import { GroupsWidgetComponent } from './widgets/groups-widget/groups-widget.component';
import { LifeActionWidgetComponent } from './widgets/life-action-widget/life-action-widget.component';
import { LeaderboardWidgetComponent } from './widgets/leaderboard-widget/leaderboard-widget.component';
import { AuthModule } from './auth/auth.module';
import { InsightsModule } from './insights/insights.module';
import { NavbarComponent } from '../components/navbar/navbar.component';
import { ProfileDropdownComponent } from './profile-dropdown/profile-dropdown.component';
import { EapBookingComponent } from './widgets/eap-booking-widget/eap-booking-widget.component';
import { HcfWidgetComponent } from './widgets/hcf-widget/hcf-widget.component';
import { NasdaqWidgetComponent } from './widgets/nasdaq-widget/nasdaq-widget.component';
import { TeamLeaveComponent } from './team-leave/team-leave.component';
import { PeerSupportWidgetComponent } from './widgets/peer-support-widget/peer-support-widget.component';
import { CompanyRegisterInfoComponent } from './company-register-info/company-register-info.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { WidgetPreviewComponent } from './company-register-info/preview/widget-preview/widget-preview.component';
import { NavigationPreviewComponent } from './company-register-info/preview/navigation-preview/navigation-preview.component';
import { HomePreviewComponent } from './company-register-info/preview/home-preview/home-preview.component';
import { LogoPreviewComponent } from './company-register-info/preview/logo-preview/logo-preview.component';
import { ArticlePreviewComponent } from './company-register-info/preview/article-preview/article-preview.component';
import { DeleteUserModalComponent } from '../components/delete-user-modal/delete-user-modal.component';
import { InsightsNavbarComponent } from '../components/navbar-insights/navbar-insights.component';
import { QRComponent } from './qr/qr.component';
import { InsightsLayoutComponent } from './layouts/insights-layout/insights-layout.component';
import { CmsLayoutComponent } from './layouts/cms-layout/cms-layout.component';
import { SettingsLayoutComponent } from './layouts/settings-layout/settings-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { SimpleLayoutComponent } from './layouts/simple-layout/simple-layout.component';

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    AlertComponent,
    InfoAlertComponent,
    DialogComponent,
    LandingPageComponent,
    MarketingPageComponent,
    QRComponent,
    ContactComponent,
    AccountComponent,
    PreferencesComponent,
    TutorialsComponent,
    WelcomeMessageComponent,
    CarouselComponent,
    CheckboxListValueAccessorDirective,
    ImageDialogComponent,
    DatasourceComponent,
    FeatureTileComponent,
    SettingsComponent,
    SubscriptionComponent,
    HoursMinutesEntryComponent,
    ProfileCreationBackgroundComponent,
    MobileAppDownloadCtaComponent,
    NotificationsDropdownComponent,
    ProfileDropdownComponent,
    WidgetsComponent,
    DownloadAppLinksComponent,
    MyRewardsWidgetComponent,
    CompetitionWidgetComponent,
    LastBadgeEarnedComponent,
    GroupsWidgetComponent,
    LifeActionWidgetComponent,
    LeaderboardWidgetComponent,
    NavbarComponent,
    PeerSupportWidgetComponent,
    EapBookingComponent,
    HcfWidgetComponent,
    NasdaqWidgetComponent,
    TeamLeaveComponent,
    CompanyRegisterInfoComponent,
    WidgetPreviewComponent,
    NavigationPreviewComponent,
    HomePreviewComponent,
    LogoPreviewComponent,
    ArticlePreviewComponent,
    DeleteUserModalComponent,
    InsightsNavbarComponent,
    InsightsLayoutComponent,
    CmsLayoutComponent,
    SettingsLayoutComponent,
    MainLayoutComponent,
    SimpleLayoutComponent,
  ],
  imports: [
    BrowserModule,
    NgbModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    MatDialogModule,
    BrowserAnimationsModule,
    NoopAnimationsModule,
    MatInputModule,
    MatSliderModule,
    ToastrModule.forRoot({
      positionClass: 'toast-top-right',
      easing: 'ease-out',
      easeTime: 300,
      tapToDismiss: false,
      closeButton: true,
      timeOut: 6000,
      extendedTimeOut: 1000,
      maxOpened: 5,
    }),
    MatDividerModule,
    ColorPickerModule,
    MatSelectModule,
    AuthModule,
    InsightsModule,
    AppRoutingModule  // Must be LAST - routing module should always be imported after feature modules
  ],
  providers: [
    Title,
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    CookieService,
    NgbActiveModal,
    httpInterceptorProviders,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
